import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
    TextField,
    Box,
    Grid,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Switch,
    FormControlLabel,
    Typography,
    Slider,
    Checkbox,
    RadioGroup,
    Radio,
    Paper,
    Divider,
    FormLabel
} from '@mui/material';
import ImageUpload from '../common/ImageUpload';
import LocationSelector from '../common/LocationSelector';
import {
    SmokingRooms as SmokingIcon,
    Pets as PetsIcon
} from '@mui/icons-material';

const ProfileForm = ({ initialData = {}, onSubmit }) => {
    const { control, handleSubmit, setValue, formState: { errors } } = useForm({
        defaultValues: {
            nickname: initialData.nickname || '',
            profileImage: initialData.profileImage || '',
            occupation: initialData.occupation || '',
            introduction: initialData.introduction || '',
            stayDuration: initialData.stayDuration || 'medium',
            moveInPreference: initialData.moveInPreference || 'within 3 months',
            sameGenderOnly: initialData.sameGenderOnly || false,
            matchingPreferences: {
                lifeRhythm: initialData.matchingPreferences?.lifeRhythm || null,
                cleanliness: initialData.matchingPreferences?.cleanliness || null,
                communication: initialData.matchingPreferences?.communication || null,
                stayingHome: initialData.matchingPreferences?.stayingHome || null,
                visitors: initialData.matchingPreferences?.visitors || null,
                smoking: initialData.matchingPreferences?.smoking || 'non-smoker',
                pets: initialData.matchingPreferences?.pets || 'no'
            },
            preferences: {
                rentRange: initialData.preferences?.rentRange || { min: 50000, max: 150000 },
                preferredAreas: initialData.preferences?.preferredAreas || [{ prefecture: '', city: '', line: '', station: '' }],
            }
        }
    });

    // 画像アップロード処理
    const handleImageUpload = async (imageUrl) => {
        try {
            const response = await fetch('/api/users/profile/image', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({ imageUrl })
            });

            if (!response.ok) {
                throw new Error('画像の更新に失敗しました');
            }

            const data = await response.json();
            setValue('profileImage', data.imageUrl);  // フォームの値を更新
        } catch (error) {
            console.error('画像アップロードエラー:', error);
            throw error;
        }
    };

    // LocationSelectorのハンドラー
    const handleLocationChange = (locationData) => {
        setValue('preferences.preferredAreas.0', {
            prefecture: locationData.prefecture?.name || '',
            city: locationData.city?.name || '',
            line: locationData.line?.name || '',
            station: locationData.station?.name || ''
        });
    };

    return (
        <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 3 }}>
            <Grid container spacing={3}>
                <Grid item xs={12}>

                    <Controller
                        name="profileImage"
                        control={control}
                        render={({ field }) => (
                            <ImageUpload
                                value={field.value}
                                onFileSelect={(url) => {
                                    handleImageUpload(url)
                                        .then(() => field.onChange(url))
                                        .catch((error) => {
                                            console.error('Image upload error:', error);
                                        });
                                }}
                                variant="profile"
                                multiple={false}
                                uploadPath="profiles"
                            />
                        )}
                    />
                </Grid>

                <Grid item xs={12}>
                    <Controller
                        name="nickname"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                fullWidth
                                label="ニックネーム"
                                helperText="ニックネームは変更できません"
                                disabled
                                InputProps={{
                                    readOnly: true,
                                }}
                            />
                        )}
                    />
                </Grid>

                <Grid item xs={12}>
                    <Controller
                        name="occupation"
                        control={control}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                fullWidth
                                label="職業"
                                helperText="20文字以下で入力してください"
                            />
                        )}
                    />
                </Grid>

                <Grid item xs={12}>
                    <Controller
                        name="sameGenderOnly"
                        control={control}
                        render={({ field }) => (
                            <>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            {...field}
                                            checked={field.value}
                                        />
                                    }
                                    label="同性からの連絡のみ受け付ける"
                                />
                                <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                                    チェックすると、異性からのメッセージ・お問い合わせをすべてブロックします。
                                    あなたの物件や募集は異性のユーザーには「連絡不可」と表示されます。
                                    安全にルームシェアパートナーを探したい方におすすめです。
                                </Typography>
                            </>
                        )}
                    />
                </Grid>

                <Grid item xs={12}>
                    <Controller
                        name="introduction"
                        control={control}
                        rules={{ maxLength: 1000 }}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                fullWidth
                                multiline
                                rows={4}
                                label="自己紹介"
                                error={!!errors.introduction}
                                helperText="1000文字以下で入力してください"
                            />
                        )}
                    />
                </Grid>

                {/* マッチング条件セクション */}
                <Grid item xs={12}>
                    <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 2, mb: 2 }}>
                        <Typography variant="h6" gutterBottom>
                            ルームメイトマッチング条件
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
                            あなたに合ったルームメイトを見つけるために、以下の項目から選択してください。各項目は未選択のままでも構いません。
                        </Typography>
                        <Divider sx={{ mb: 3 }} />

                        <Grid container spacing={3}>
                            {/* 生活リズム */}
                            <Grid item xs={12} sm={6}>
                                <Controller
                                    name="matchingPreferences.lifeRhythm"
                                    control={control}
                                    render={({ field }) => (
                                        <FormControl component="fieldset">
                                            <Typography variant="subtitle1" gutterBottom>
                                                生活リズム
                                            </Typography>
                                            <RadioGroup
                                                {...field}
                                                onChange={(e) => field.onChange(e.target.value)}
                                                value={field.value || ''}
                                            >
                                                <FormControlLabel
                                                    value="morning"
                                                    control={<Radio />}
                                                    label="朝型（23時までに就寝、7時までに起床が多い）"
                                                />
                                                <FormControlLabel
                                                    value="night"
                                                    control={<Radio />}
                                                    label="夜型（24時以降に就寝、8時以降に起床が多い）"
                                                />
                                            </RadioGroup>
                                        </FormControl>
                                    )}
                                />
                            </Grid>

                            {/* 清潔さ */}
                            <Grid item xs={12} sm={6}>
                                <Controller
                                    name="matchingPreferences.cleanliness"
                                    control={control}
                                    render={({ field }) => (
                                        <FormControl component="fieldset">
                                            <Typography variant="subtitle1" gutterBottom>
                                                清潔さに対する姿勢
                                            </Typography>
                                            <RadioGroup
                                                {...field}
                                                onChange={(e) => field.onChange(e.target.value)}
                                                value={field.value || ''}
                                            >
                                                <FormControlLabel
                                                    value="clean"
                                                    control={<Radio />}
                                                    label="きれい好き（定期的に掃除、物は常に整理整頓）"
                                                />
                                                <FormControlLabel
                                                    value="relaxed"
                                                    control={<Radio />}
                                                    label="ある程度の散らかりは気にならない"
                                                />
                                            </RadioGroup>
                                        </FormControl>
                                    )}
                                />
                            </Grid>

                            {/* コミュニケーション */}
                            <Grid item xs={12} sm={6}>
                                <Controller
                                    name="matchingPreferences.communication"
                                    control={control}
                                    render={({ field }) => (
                                        <FormControl component="fieldset">
                                            <Typography variant="subtitle1" gutterBottom>
                                                コミュニケーションスタイル
                                            </Typography>
                                            <RadioGroup
                                                {...field}
                                                onChange={(e) => field.onChange(e.target.value)}
                                                value={field.value || ''}
                                            >
                                                <FormControlLabel
                                                    value="active"
                                                    control={<Radio />}
                                                    label="交流型（会話や食事など、ある程度の交流を希望）"
                                                />
                                                <FormControlLabel
                                                    value="minimal"
                                                    control={<Radio />}
                                                    label="独立型（プライバシーを重視、必要最低限の交流で十分）"
                                                />
                                            </RadioGroup>
                                        </FormControl>
                                    )}
                                />
                            </Grid>

                            {/* 在宅時間 */}
                            <Grid item xs={12} sm={6}>
                                <Controller
                                    name="matchingPreferences.stayingHome"
                                    control={control}
                                    render={({ field }) => (
                                        <FormControl component="fieldset">
                                            <Typography variant="subtitle1" gutterBottom>
                                                在宅状況
                                            </Typography>
                                            <RadioGroup
                                                {...field}
                                                onChange={(e) => field.onChange(e.target.value)}
                                                value={field.value || ''}
                                            >
                                                <FormControlLabel
                                                    value="often"
                                                    control={<Radio />}
                                                    label="在宅が多い（リモートワークや在宅時間が長い）"
                                                />
                                                <FormControlLabel
                                                    value="rarely"
                                                    control={<Radio />}
                                                    label="外出が多い（日中はほとんど家にいない）"
                                                />
                                            </RadioGroup>
                                        </FormControl>
                                    )}
                                />
                            </Grid>

                            {/* 来客 */}
                            <Grid item xs={12} sm={6}>
                                <Controller
                                    name="matchingPreferences.visitors"
                                    control={control}
                                    render={({ field }) => (
                                        <FormControl component="fieldset">
                                            <Typography variant="subtitle1" gutterBottom>
                                                友人や知人を家に招くこと
                                            </Typography>
                                            <RadioGroup
                                                {...field}
                                                onChange={(e) => field.onChange(e.target.value)}
                                                value={field.value || ''}
                                            >
                                                <FormControlLabel
                                                    value="welcome"
                                                    control={<Radio />}
                                                    label="招くことがある（月に数回程度は友人を招きたい）"
                                                />
                                                <FormControlLabel
                                                    value="rarely"
                                                    control={<Radio />}
                                                    label="ほとんど招かない（特別な場合を除き、外で会うことが多い）"
                                                />
                                            </RadioGroup>
                                        </FormControl>
                                    )}
                                />
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>

                <Grid item xs={12}>
                    <Typography gutterBottom>ルームメイトマッチング条件</Typography>
                    <Grid container spacing={2}>
                        {/* 喫煙 */}
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <SmokingIcon sx={{ mr: 1 }} />
                                    <Typography variant="subtitle1">
                                        喫煙
                                    </Typography>
                                </Box>
                                <Controller
                                    name="matchingPreferences.smoking"
                                    control={control}
                                    defaultValue="non-smoker"
                                    render={({ field }) => (
                                        <RadioGroup {...field}>
                                            <FormControlLabel value="non-smoker" control={<Radio />} label="喫煙しない" />
                                            <FormControlLabel value="smoker" control={<Radio />} label="喫煙する" />
                                        </RadioGroup>
                                    )}
                                />
                            </Paper>
                        </Grid>

                        {/* ペット */}
                        <Grid item xs={12} md={6}>
                            <Paper sx={{ p: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <PetsIcon sx={{ mr: 1 }} />
                                    <Typography variant="subtitle1">
                                        ペット
                                    </Typography>
                                </Box>
                                <Controller
                                    name="matchingPreferences.pets"
                                    control={control}
                                    defaultValue="no"
                                    render={({ field }) => (
                                        <RadioGroup {...field}>
                                            <FormControlLabel value="no" control={<Radio />} label="ペットを飼っていない" />
                                            <FormControlLabel value="yes" control={<Radio />} label="ペットを飼っている" />
                                        </RadioGroup>
                                    )}
                                />
                            </Paper>
                        </Grid>

                        {/* 他のマッチング条件はこの下に配置 */}
                    </Grid>
                </Grid>

                <Grid item xs={12}>
                    <Typography gutterBottom>希望家賃範囲</Typography>
                    <Controller
                        name="preferences.rentRange"
                        control={control}
                        render={({ field }) => (
                            <Box sx={{ px: 2 }}>
                                <Slider
                                    value={[field.value?.min || 50000, field.value?.max || 150000]}
                                    onChange={(_, newValue) => {
                                        field.onChange({
                                            min: newValue[0],
                                            max: newValue[1]
                                        });
                                    }}
                                    valueLabelDisplay="auto"
                                    min={0}
                                    max={1000000}
                                    step={10000}
                                    valueLabelFormat={(value) => `¥${value.toLocaleString()}`}
                                />
                            </Box>
                        )}
                    />
                </Grid>

                <Grid item xs={12} sm={6}>
                    <Controller
                        name="stayDuration"
                        control={control}
                        render={({ field }) => (
                            <FormControl fullWidth>
                                <InputLabel>居住希望期間</InputLabel>
                                <Select {...field} label="居住希望期間">
                                    <MenuItem value="short">短期（1-3ヶ月）</MenuItem>
                                    <MenuItem value="medium">中期（3-6ヶ月）</MenuItem>
                                    <MenuItem value="long">長期（6ヶ月以上）</MenuItem>
                                </Select>
                            </FormControl>
                        )}
                    />
                </Grid>

                <Grid item xs={12} sm={6}>
                    <Controller
                        name="moveInPreference"
                        control={control}
                        render={({ field }) => (
                            <FormControl fullWidth>
                                <InputLabel>入居希望時期</InputLabel>
                                <Select {...field} label="入居希望時期">
                                    <MenuItem value="within 1 month">1ヶ月以内</MenuItem>
                                    <MenuItem value="within 3 months">3ヶ月以内</MenuItem>
                                    <MenuItem value="within 6 months">6ヶ月以内</MenuItem>
                                    <MenuItem value="more than 6 months">6ヶ月以上先</MenuItem>
                                </Select>
                            </FormControl>
                        )}
                    />
                </Grid>

                <Grid item xs={12}>
                    <Typography gutterBottom>希望エリア</Typography>
                    <LocationSelector
                        onChange={handleLocationChange}
                        initialValues={{
                            prefecture: initialData.preferences?.preferredAreas?.[0]?.prefecture,
                            city: initialData.preferences?.preferredAreas?.[0]?.city,
                            line: initialData.preferences?.preferredAreas?.[0]?.line,
                            station: initialData.preferences?.preferredAreas?.[0]?.station
                        }}
                    />
                </Grid>

                <Grid item xs={12}>
                    <Button
                        type="submit"
                        variant="contained"
                        fullWidth
                        size="large"
                    >
                        保存する
                    </Button>
                </Grid>
            </Grid>
        </Box>
    );
};

export default ProfileForm; 